<script>
  import { setContext } from "svelte";
  import { Router, Route, Link } from "svelte-routing";

  import ProfileIcon from "@local/assets/icons/profile.svg";
  import OrgIcon from "@local/assets/icons/group.svg";
  import AddressCardIcon from "@local/assets/icons/address-card.svg";
  import PeopleIcon from "@local/assets/icons/people.svg";
  import MailIcon from "@local/assets/icons/mail.svg";
  import GearIcon from "@local/assets/icons/gear.svg";
  import InvitationsIcon from "@local/assets/icons/invitations.svg";
  import LedgerIcon from "@local/assets/icons/ledger.svg";

  import Navbar from "#lib/Navbar.svelte";
  import ProfileSettings from "#lib/settings/ProfileSettings.svelte";
  import OrgSettings from "#lib/settings/OrgSettings.svelte";
  import AccountSettings from "#lib/settings/AccountSettings.svelte";
  import OrgUsers from "#lib/settings/OrgUsers.svelte";
  import UserSettings from "#lib/settings/UserSettings.svelte";
  import AddressBook from "#lib/settings/AddressBook.svelte";
  import Customers from "#lib/settings/Customers.svelte";
  import ProductCustomers from "#lib/settings/ProductCustomers.svelte";
  import ProductCustomer from "#lib/settings/ProductCustomerSettings.svelte";
  import Reps from "#lib/settings/Reps.svelte";
  import Customer from "#lib/settings/CustomerSettings.svelte";
  import EmailPreferences from "#lib/settings/EmailPreferences.svelte";
  import Invitations from "#lib/settings/Invitations.svelte";
  import DeveloperSettings from "#lib/settings/DeveloperSettings.svelte";

  import { orgs } from "#src/stores/organizations.js";
  import { profile } from "#src/stores/auth.js";
  import { createOrg } from "#src/stores/org.js";
  import { getCustomerId } from "#src/api/stripe.js";

  export let path = undefined;
  export let queryParams;

  const org = createOrg();
  setContext("org", org);

  $: orgid = $profile?.organization_id;
  $: $orgs[orgid] && org.init($orgs[orgid]);
</script>

<Navbar />
{#if $org}
  <div class="grow overflow-auto bg-gray-100 p-4">
    <h2 class="p-4">Settings</h2>
    <div class="flex flex-col sm:flex-row gap-2 text-sm">
      <div class="p-4 sm:w-64 nav-list">
        <Link to="/settings/profile">
          <button class:selected={path === "profile" || !path}>
            <ProfileIcon />
            <div class="hidden sm:block">Profile</div>
          </button>
        </Link>
        <Link to="/settings/org">
          <button class:selected={path === "org"}>
            <OrgIcon />
            <div class="hidden sm:block truncate">{$org.name}</div>
          </button>
        </Link>
        {#await getCustomerId($org?.id) then res}
          {#if res.customer_id}
            <Link to="/settings/account">
              <button class:selected={path === "account"}>
                <LedgerIcon />
                <div class="hidden sm:block">Account</div>
              </button>
            </Link>
          {/if}
        {/await}
        <Link to="/settings/users">
          <button class:selected={path === "users"}>
            <PeopleIcon />
            <div class="hidden sm:block">Team</div>
          </button>
        </Link>
        {#if $org?.org_type === "recipient"}
          <Link to="/settings/contacts">
            <button class:selected={path === "contacts"}>
              <AddressCardIcon />
              <div class="hidden sm:block">Contacts</div>
            </button>
          </Link>
        {:else if $org?.org_type === "supplier"}
          <Link to="/settings/reps">
            <button class:selected={path === "reps"}>
              <PeopleIcon />
              <div class="hidden sm:block">Reps</div>
            </button>
          </Link>
          <Link to="/settings/customers">
            <button class:selected={path === "customers"}>
              <AddressCardIcon />
              <div class="hidden sm:block">Customers</div>
            </button>
          </Link>
          <!-- <Link to="/settings/product_customers">
            <button class:selected={path === "product_customers"}>
              <AddressCardIcon />
              <div class="hidden sm:block">Product Customers</div>
            </button>
          </Link> -->
        {/if}
        <Link to="/settings/email-preferences">
          <button class:selected={path === "email-preferences"}>
            <MailIcon />
            <div class="hidden sm:block">Email Preferences</div>
          </button>
        </Link>
        {#if $org?.org_type === "recipient"}
          <Link to="/settings/invitations">
            <button class:selected={path === "invitations"}>
              <InvitationsIcon />
              <div class="hidden sm:block">Invitations</div>
            </button>
          </Link>
        {/if}
        {#if $profile?.user_role === "developer"}
          <Link to="/settings/developer">
            <button class:selected={path === "developer"}>
              <GearIcon />
              <div class="hidden sm:block">Developer Settings</div>
            </button>
          </Link>
        {/if}
      </div>

      <div class="grow py-4 sm:px-4 overflow-hidden">
        <Router>
          <Route path="org" component={OrgSettings} {org} manageable />
          <Route path="account" component={AccountSettings} {org} />
          <Route path="users" component={OrgUsers} />
          <Route path="users/:profileid" component={UserSettings} />
          <Route path="contacts" component={AddressBook} />
          <Route path="customers" component={Customers} />
          <Route path="customers/:customerid" component={Customer} />
          <Route path="product_customers" component={ProductCustomers} {queryParams} />
          <Route path="product_customers/:customerid" component={ProductCustomer} />
          <Route path="reps" component={Reps} />
          <Route path="email-preferences" component={EmailPreferences} />
          <Route path="invitations" component={Invitations} />
          <Route path="developer" component={DeveloperSettings} />
          <Route path="/*" component={ProfileSettings} profile={$profile} isUser />
        </Router>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  .nav-list {
    @apply flex gap-2 items-stretch;
    flex-direction: row;

    @media (min-width: 640px) {
      flex-direction: column;
    }

    button {
      @apply flex gap-2 items-center p-2 rounded w-full;

      &.selected,
      &:hover {
        @apply bg-gray-200;
      }
    }
  }
</style>
